import { Form, Spin } from "antd"
import { useEffect, useState, useRef } from "react"
import { ListingActionButtons } from "layoutsV2/pro/ProNewListingLayout/ListingActionButtons"
import { serialize } from "object-to-formdata"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import { modelSelector } from "store/selectors"
import { updateTrip, getGuideTrip, getCategories, getMarketplaceTags, getExperienceOptions, cleanTrip } from "store/trips"
import { CustomForm } from "ui/Form"
import { AdditionalDetails } from "./AdditionalDetails"
import { ExperienceDetails } from "./ExperienceDetails"
import { ExperienceInfo } from "./ExperienceInfo"
import { PublishingSettings } from "./PublishingSettings"
import { setFormValues, setFormTouched } from "store/proNewListingForms"

export const ListingDetails = () => {
  const [form] = Form.useForm()
  const dispatch = useDispatch()
  const { id } = useParams()
  const trip = useSelector(modelSelector("trip"))
  const tripLoading = useSelector((state) => state.trips.trip.loading)
  const categoriesData = useSelector((state) => state.trips.categories.data)
  const [selectedLanguages, setSelectedLanguages] = useState([])
  const [selectedSkillLevels, setSelectedSkillLevels] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const formDetails = useSelector((state) => state.proNewListingForms.details)
  const hasInitialized = useRef(false)

  useEffect(() => {
    if (id) {
      dispatch(getGuideTrip(id))
      dispatch(getCategories())
      dispatch(getMarketplaceTags())
      dispatch(getExperienceOptions())
    }
    return () => {
      dispatch(cleanTrip())
    }
  }, [dispatch, id])

  // Reset hasInitialized when navigating to a different trip
  useEffect(() => {
    hasInitialized.current = false
  }, [id])

  useEffect(() => {
    // Only initialize if we have trip data AND it matches the current ID
    if (trip?.id === Number(id) && !hasInitialized.current) {
      // Normalize trip.categories — API returns [{ id, name }] objects; form stores names only
      let rawCategories = []
      if (Array.isArray(trip.categories)) {
        rawCategories = trip.categories
      } else if (trip.categories) {
        rawCategories = [trip.categories]
      }
      const availableCats = categoriesData?.categories || []
      const categoryNames = rawCategories.map((c) => {
        const name = typeof c === "string" ? c : c.name
        const match = availableCats.find((a) => a.name.toLowerCase() === name.toLowerCase())
        return match ? match.name : name
      })

      const marketplaceTags = trip.marketplace_tag_ids?.length
        ? trip.marketplace_tag_ids
        : trip.marketplace_tags || undefined

      form.setFieldsValue({
        title: trip.title,
        description: trip.description,
        categories: categoryNames.length ? categoryNames : undefined,
        marketplace_tags: marketplaceTags?.length ? marketplaceTags : undefined,
        min_guests: trip.min_guests,
        max_guests: trip.max_guests,
        languages: trip.languages,
        skill_levels: trip.skill_levels,
        included_items: trip.included_items,
        bring_items: trip.bring_items,
        accessibility_enabled: trip.accessibility_enabled,
        alcohol_allowed: trip.alcohol_allowed,
        children_allowed: trip.children_allowed,
        publish_on_guides_site: trip.publish_on_guides_site,
        publish_on_platform: trip.publish_on_platform
      })
      // Mark as initialized after setting values
      hasInitialized.current = true

      // Reset touched state to false after initialization (edit mode)
      // This prevents auto-save when switching tabs without making changes
      if (id) {
        dispatch(setFormTouched("details", false))
      }
    }
  }, [trip, form, id, dispatch])

  const onFinish = (callback) => {
    if (!id) return

    form.validateFields().then(() => {
      const formData = createFormData()
      handleFormSubmission(formData, callback)
    })
  }

  const createFormData = () => {
    const formValues = form.getFieldsValue()

    const categories = formValues.categories || []
    const includedItems = formValues.included_items || []
    const bringItems = formValues.bring_items || []

    // Split selected names: known categories → send ID, custom typed → send name
    const availableCats = categoriesData?.categories || []
    const category_ids = []
    const new_category_names = []
    categories.forEach((name) => {
      const found = availableCats.find((c) => c.name === name)
      if (found) {
        category_ids.push(found.id)
      } else {
        new_category_names.push(name)
      }
    })

    const payload = {
      ...formValues,
      price_per_person: 10,
      skill_level_ids: selectedSkillLevels,
      language_ids: selectedLanguages,
      marketplace_tag_ids: formValues.marketplace_tags || [],
      category_ids,
      new_category_names,
      included_item_ids: includedItems,
      bring_item_ids: bringItems
    }
    return serialize({ trip: payload }, { allowEmptyArrays: true })
  }

  const handleFormSubmission = (formData, callback) => {
    setIsLoading(true)
    dispatch(updateTrip(id, formData))
      .then(() => {
        dispatch(setFormTouched("details", false))
        setIsLoading(false)
        callback?.()
      })
      .catch(() => {
        setIsLoading(false)
      })
  }

  useEffect(() => {
    const formValues = form.getFieldsValue()
    const categories = formValues.categories || []
    const includedItems = formValues.included_items || []
    const bringItems = formValues.bring_items || []
    const allValues = {
      ...formValues,
      price_per_person: 10,
      skill_levels: selectedSkillLevels,
      languages: selectedLanguages,
      trip_categories_attributes: categories?.map((id) => ({ name: id })),
      trip_included_items_attributes: includedItems?.map((id) => ({ name: id })),
      trip_bring_items_attributes: bringItems?.map((id) => ({ name: id }))
    }
    dispatch(setFormValues("details", allValues))
  }, [dispatch, trip, selectedLanguages, selectedSkillLevels, form])

  const handleValuesChange = (_, allValues) => {
    const currentValues = {
      ...formDetails?.values,
      ...allValues
    }

    dispatch(setFormValues("details", currentValues))
    // Only mark as touched if initialization is complete
    if (hasInitialized.current && !formDetails?.touched) {
      dispatch(setFormTouched("details", true))
    }
  }

  const handleLanguageInteraction = () => {
    dispatch(setFormTouched("details", true))
  }

  const handleSkillInteraction = () => {
    dispatch(setFormTouched("details", true))
  }

  const handleDescriptionChange = () => {
    // Sync current form values to Redux (including the new description)
    const formValues = form.getFieldsValue()
    const categories = formValues.categories || []
    const includedItems = formValues.included_items || []
    const bringItems = formValues.bring_items || []
    const allValues = {
      ...formValues,
      price_per_person: 10,
      skill_levels: selectedSkillLevels,
      languages: selectedLanguages,
      trip_categories_attributes: categories?.map((id) => ({ name: id })),
      trip_included_items_attributes: includedItems?.map((id) => ({ name: id })),
      trip_bring_items_attributes: bringItems?.map((id) => ({ name: id }))
    }
    dispatch(setFormValues("details", allValues))
    dispatch(setFormTouched("details", true))
  }

  return (
    <Spin spinning={tripLoading || isLoading} size="large" style={{ width: "100%" }}>
      <CustomForm
        form={form}
        onValuesChange={handleValuesChange}
        initialValues={trip}
        layout="vertical"
        style={{ maxWidth: "984px" }}
      >
        <ExperienceInfo form={form} onDescriptionChange={handleDescriptionChange} />
        <ExperienceDetails
          form={form}
          setSelectedLanguages={setSelectedLanguages}
          selectedLanguages={selectedLanguages}
          selectedSkillLevels={selectedSkillLevels}
          setSelectedSkillLevels={setSelectedSkillLevels}
          onLanguageInteraction={handleLanguageInteraction}
          onSkillInteraction={handleSkillInteraction}
        />
        <AdditionalDetails form={form} />
        <PublishingSettings form={form} />
      </CustomForm>
      <ListingActionButtons form={form} onFinish={onFinish} />
    </Spin>
  )
}
